import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    Paper,
    Container,
    IconButton,
    CircularProgress,
    Checkbox,
    Toolbar,
    Fade,
    Menu,
    MenuItem,
} from '@mui/material';
import MarkdownContent from './markdownContent';
import ChartContent from './chartContent';
import FollowupQuestions from './followupQuestions';
import NodeProgress from './nodeProgress';
import './chatMessages.less';
import '../../../../globals/styles/variables.less';
import xlsFileImage from '../../../../assets/images/xls-file.png';
import pptFileImage from '../../../../assets/images/ppt.png';
import AnswerAnalysisModal from '../modals/answerAnalysisModal';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import DeselectIcon from '@mui/icons-material/Deselect';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ShareIcon from '@mui/icons-material/Share';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { generatePPT, generatePPTForSelectedMsgs } from '../../../../api/chatAPI';
import { useSelection } from '../contexts/SelectionContext';

const Typewriter = ({
    content,
    speed = 1,
    charsPerFrame = 5,
    onComplete,
    messagesEndRef,
    isStreaming,
    isFromHistory,
}) => {
    const [displayedContent, setDisplayedContent] = useState('');
    const [index, setIndex] = useState(0);

    useEffect(() => {
        if (isFromHistory) {
            setDisplayedContent(content);
            setIndex(content.length);

            if (onComplete) {
                onComplete();
            }
            return;
        }
        setDisplayedContent('');
        setIndex(0);
    }, [content, isFromHistory]);

    useEffect(() => {
        if (isFromHistory) {
            return;
        }
        if (index < content.length) {
            const timeout = setTimeout(() => {
                const nextIndex = Math.min(index + charsPerFrame, content.length);
                setDisplayedContent(content.slice(0, nextIndex));
                setIndex(nextIndex);
            }, speed);
            return () => clearTimeout(timeout);
        } else if (index >= content.length && onComplete) {
            onComplete();
        }
    }, [index, content, speed, charsPerFrame, onComplete, isStreaming, isFromHistory]);

    return <MarkdownContent content={displayedContent} />;
};

const extractFilename = (urlString) => {
    if (!urlString) return null;
    const parsedUrl = new URL(urlString);
    const pathname = parsedUrl.pathname;
    const fullFilename = pathname.split('/').pop();
    return fullFilename;
};

const Message = ({
    content,
    isUser,
    chartData,
    followupQuestions,
    excelFileURL,
    pptFileURL,
    downloadExcelFile,
    downloadPPTFile,
    msgsCount,
    index,
    onQuestionClick,
    isClarificationQuestion,
    openAnalysis,
    transparency,
    onGeneratePPTClick,
    pptLoading,
    run_id,
    nodeProgress,
    messagesEndRef,
    isStreaming,
    isFromHistory,
    isSelectionMode,
    isSelected,
    onSelectMessage,
}) => {
    const ExcelfileName = extractFilename(excelFileURL);
    const PPTfileName = extractFilename(pptFileURL);
    const [isTypingComplete, setIsTypingComplete] = useState(isFromHistory || !isStreaming);
    // console.log('Message: Rendering', {
    //     run_id,
    //     isUser,
    //     content: content.slice(0, 50) + '...',
    //     chartData: chartData ? 'Chart data present' : 'No chart data',
    //     nodeProgress,
    //     index,
    //     msgsCount,
    //     isStreaming,
    //     isFromHistory,
    //     isSelectionMode,
    //     isSelected,
    // });

    const showNodeProgress = !isUser && nodeProgress && nodeProgress.length > 0;
    const isProcessing = !isUser && (!content || content === 'Processing...');

    const handleMessageClick = (event) => {
        if (isSelectionMode && !isUser) {
            event.stopPropagation();
            onSelectMessage(run_id);
        }
    };

    const handleCheckboxClick = (event) => {
        event.stopPropagation();
        onSelectMessage(run_id);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: isUser ? 'flex-end' : 'flex-start',
                mb: 2,
                maxWidth: '100%',
                marginBottom: index === msgsCount - 1 ? '8rem' : 2,
                position: 'relative',
                cursor: isSelectionMode && !isUser ? 'pointer' : 'default',
            }}
            className="individual-message-wrapper"
            onClick={handleMessageClick}
        >
            {isSelectionMode && !isUser && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: -20,
                        top: 8,
                        zIndex: 10,
                    }}
                >
                    <Checkbox
                        checked={isSelected}
                        onChange={handleCheckboxClick}
                        onClick={handleCheckboxClick}
                        size="small"
                        sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            borderRadius: '50%',
                            padding: '5px',
                            '&.Mui-checked': {
                                backgroundColor: '#44338A',
                                color: 'white',
                            },
                        }}
                    />
                </Box>
            )}
            {isUser ? (
                <Paper
                    elevation={1}
                    className="individual-message-paper"
                    sx={{
                        padding: '12px 18px',
                        maxWidth: isUser ? '70%' : '90%',
                        backgroundColor: isSelected ? '#E8D3DD80' : isUser ? '#0000000a' : '#FFFFFF',
                        color: isUser ? '#242424' : 'text.primary',
                        borderRadius: isUser ? '60px' : '24px',
                        boxShadow: isUser ? 'none' : '0px 2px 8px rgba(0, 0, 0, 0.04)',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        wordBreak: 'break-word',
                        hyphens: 'auto',
                        border: isSelected ? '2px solid #44338A' : 'none',
                        transition: 'all 0.2s ease-in-out',
                    }}
                >
                    <Typography
                        className="user-message"
                        sx={{
                            fontSize: '14px',
                            overflowWrap: 'break-word',
                            wordBreak: 'break-word',
                            wordWrap: 'break-word',
                        }}
                    >
                        {content}
                    </Typography>
                </Paper>
            ) : (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        maxWidth: '90%',
                        marginLeft: isSelectionMode ? '20px' : 'unset ',
                    }}
                >
                    {showNodeProgress && (
                        <Box sx={{ mb: 1 }}>
                            <NodeProgress nodes={nodeProgress} />
                        </Box>
                    )}
                    {(content || isProcessing) && (
                        <Paper
                            elevation={1}
                            className="individual-message-paper"
                            sx={{
                                padding: '12px 18px',
                                maxWidth: '100%',
                                backgroundColor: isSelected ? '#F5F5F580' : '#FFFFFF',
                                color: 'text.primary',
                                borderRadius: '24px',
                                boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.04)',
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word',
                                wordBreak: 'break-word',
                                hyphens: 'auto',
                                border: isSelected ? '2px solid #44338A' : 'none',
                                transition: 'all 0.2s ease-in-out',
                            }}
                        >
                            <Box className="ai-message">
                                {isProcessing ? (
                                    <Typography sx={{ color: 'gray' }}>Processing...</Typography>
                                ) : (
                                    <Typewriter
                                        content={content}
                                        speed={1}
                                        charsPerFrame={5}
                                        onComplete={() => setIsTypingComplete(true)}
                                        messagesEndRef={messagesEndRef}
                                        isStreaming={isStreaming}
                                        isFrom
                                        Tylko
                                        isFromHistory={isFromHistory}
                                    />
                                )}
                                {isTypingComplete && chartData && <ChartContent chartData={chartData} />}
                                {isTypingComplete && (
                                    <FollowupQuestions
                                        questions={followupQuestions}
                                        onQuestionClick={onQuestionClick}
                                    />
                                )}
                                {isTypingComplete && isClarificationQuestion > 0 && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            gap: 2,
                                            marginTop: 4,
                                        }}
                                    >
                                        {!isFromHistory && (
                                            <Button
                                                variant="contained"
                                                sx={{
                                                    background: 'none',
                                                    minWidth: 'initial',
                                                    padding: '2px 0px',
                                                    boxShadow: 'none',
                                                    fontSize: '14px',
                                                    textTransform: 'none',
                                                }}
                                                onClick={() => {
                                                    downloadExcelFile(excelFileURL);
                                                }}
                                                // startIcon={<img src={xlsFileImage} width={30} height={30} />}
                                            >
                                                {<img src={xlsFileImage} width={30} height={30} />}
                                                {/* Download Excel */}
                                            </Button>
                                        )}

                                        <Button
                                            variant="contained"
                                            sx={{
                                                background: 'none',
                                                minWidth: 'initial',
                                                padding: '2px 0px',
                                                boxShadow: 'none',
                                                fontSize: '14px',
                                                textTransform: 'none',
                                            }}
                                            onClick={() => {
                                                onGeneratePPTClick();
                                            }}
                                            // startIcon={
                                            //     pptLoading ? (
                                            //         <CircularProgress size={20} sx={{ color: '#44338A' }} />
                                            //     ) : (
                                            //         <img src={pptFileImage} width={30} height={30} />
                                            //     )
                                            // }
                                        >
                                            {pptLoading ? (
                                                <CircularProgress size={20} sx={{ color: '#44338A' }} />
                                            ) : (
                                                <img src={pptFileImage} width={30} height={30} />
                                            )}
                                            {/* Download PPT */}
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                    )}
                    {isTypingComplete && isClarificationQuestion > 0 && (
                        <Box sx={{ display: 'flex', width: '100%' }}>
                            <Button
                                variant="text"
                                sx={{
                                    background: 'none',
                                    color: '#8C8C8C',
                                    padding: '10px',
                                    fontSize: '14px',
                                    fontWeight: '400',
                                    '&:hover': { background: 'none' },
                                }}
                                onClick={() => openAnalysis()}
                                endIcon={<ArrowForwardIosIcon />}
                            >
                                Show Analysis
                            </Button>
                            <div style={{ flex: 1 }} />
                            <div style={{}}>
                                <IconButton className="appbar-icons-button" sx={{ color: '#5D5D5D66' }} size="small">
                                    <ThumbDownAltOutlinedIcon />
                                </IconButton>
                                <IconButton className="appbar-icons-button" sx={{ color: '#5D5D5D66' }} size="small">
                                    <ThumbUpAltOutlinedIcon />
                                </IconButton>
                            </div>
                        </Box>
                    )}
                </div>
            )}
        </Box>
    );
};

const SelectionToolbar = ({
    selectedCount,
    totalCount,
    onSelectAll,
    onDeselectAll,
    onDeleteSelected,
    onExportSelected,
    selectedMsgPPTGeneration,
    onCancelSelection,
    pptGenerating,
}) => {
    const [anchorEl, setAnchorEl] = useState(null);

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    return (
        <Fade in={selectedCount > 0}>
            <Paper
                elevation={4}
                sx={{
                    position: 'fixed',
                    top: 16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 1000,
                    borderRadius: '24px',
                    overflow: 'hidden',
                }}
            >
                <Toolbar
                    sx={{
                        backgroundColor: '#44338A',
                        color: 'white',
                        minHeight: '45px !important',
                        paddingX: 2,
                        gap: 1,
                    }}
                >
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '14px' }}>
                        {selectedCount} of {totalCount} selected
                    </Typography>
                    <IconButton
                        color="inherit"
                        onClick={selectedCount === totalCount ? onDeselectAll : onSelectAll}
                        title={selectedCount === totalCount ? 'Deselect All' : 'Select All'}
                    >
                        {selectedCount === totalCount ? <DeselectIcon /> : <SelectAllIcon />}
                    </IconButton>
                    <IconButton color="inherit" onClick={onExportSelected} title="Download">
                        <FileDownloadIcon />
                    </IconButton>
                    <IconButton color="inherit" onClick={selectedMsgPPTGeneration} title="Generate PPT">
                        {/* <img src={pptFileImage} width={30} height={30} /> */}
                        {pptGenerating ? (
                            <CircularProgress size={20} sx={{ color: 'white' }} />
                        ) : (
                            <img src={pptFileImage} width={30} height={30} />
                        )}
                    </IconButton>
                    {/* <IconButton disabled={true} color="inherit" onClick={handleMenuOpen} title="More Actions">
                        <MoreVertIcon />
                    </IconButton> */}
                    <Button
                        color="inherit"
                        onClick={onCancelSelection}
                        sx={{ ml: 2, textTransform: 'none', fontSize: '14px' }}
                    >
                        Cancel
                    </Button>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                        PaperProps={{
                            sx: { mt: 1 },
                        }}
                    >
                        <MenuItem
                            onClick={() => {
                                onDeleteSelected();
                                handleMenuClose();
                            }}
                        >
                            <DeleteIcon sx={{ mr: 1 }} />
                            Delete Selected
                        </MenuItem>
                    </Menu>
                </Toolbar>
            </Paper>
        </Fade>
    );
};

function ChatMessages({
    messages = [],
    messagesEndRef,
    onQuestionClick,
    chatSessionUuid,
    nodeProgress = [],
    isStreaming,
}) {
    const [openModal, setOpenModal] = useState(false);
    const [analysis, setAnalysis] = useState('');
    const [pptLoading, setPptLoading] = useState(false);
    const [pptGenerating, setPptGenerating] = useState(false);

    const [processedMessages, setProcessedMessages] = useState([]);
    const {
        isSelectionMode,
        setIsSelectionMode,
        selectedMessages,
        setSelectedMessages,
        selectableAIMessages,
        handleSelectAll,
        handleDeselectAll,
        setSelectableAIMessages,
    } = useSelection();

    // console.log('ChatMessages: Rendering', {
    //     messagesLength: messages.length,
    //     nodeProgressLength: nodeProgress.length,
    //     isStreaming,
    //     isSelectionMode,
    //     selectedCount: selectedMessages.size,
    // });
    // console.log(selectedMessages, "--------", messages)
    useEffect(() => {
        if (processedMessages.length) {
            const aIMessages = processedMessages.filter(
                (msg) => !msg.isUser && msg.content && msg.content.trim() !== '',
            );
            setSelectableAIMessages(aIMessages);
        }
    }, [processedMessages]);
    useEffect(() => {
        let newProcessedMessages = [...messages];

        if (messages.length === 0 && nodeProgress.length > 0) {
            newProcessedMessages = [
                {
                    content: '',
                    isUser: false,
                    nodeProgress: nodeProgress,
                    run_id: `placeholder-${Date.now()}`,
                    isFromHistory: false,
                },
            ];
        } else if (messages.length > 0) {
            const lastMessage = messages[messages.length - 1];
            if (lastMessage.isUser && nodeProgress.length > 0) {
                newProcessedMessages.push({
                    content: '',
                    isUser: false,
                    nodeProgress: nodeProgress,
                    run_id: `placeholder-${Date.now()}`,
                    isFromHistory: false,
                });
            } else if (!lastMessage.isUser) {
                newProcessedMessages = messages.map((msg, idx) => {
                    if (idx === messages.length - 1 && !msg.isUser) {
                        return { ...msg, nodeProgress: nodeProgress };
                    }
                    return { ...msg, nodeProgress: msg.nodeProgress || [] };
                });
            }
        }

        setProcessedMessages(newProcessedMessages);
        // console.log('useEffect[messages, nodeProgress]: Processed messages updated, length:', newProcessedMessages.length);
    }, [messages, nodeProgress]);

    const handleSelectMessage = (run_id) => {
        console.log('handleSelectMessage called with:', run_id);
        const newSet = new Set(selectedMessages);
        if (newSet.has(run_id)) {
            newSet.delete(run_id);
            console.log('Message deselected:', run_id);
        } else {
            newSet.add(run_id);
            console.log('Message selected:', run_id);
        }
        console.log('New selected messages:', Array.from(newSet));
        setSelectedMessages(newSet);
    };

    const handleDeleteSelected = () => {
        // console.log('Deleting AI messages:', Array.from(selectedMessages));
        setSelectedMessages(new Set());
        setIsSelectionMode(false);
    };

    const handleExportSelected = () => {
        const selectedMessagesData = processedMessages.filter((msg) => !msg.isUser && selectedMessages.has(msg.run_id));
        // console.log('Exporting AI messages:', selectedMessagesData);

        const dataStr = JSON.stringify(selectedMessagesData, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `selected-ai-messages-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };
    const handlePPTForSelected = async () => {
        const selectedArray = Array.from(selectedMessages);
        try {
            setPptGenerating(true);
            // console.log(selectedArray, 'selectedMessages Generating PPT with params:', selectedMessages);
            const params = {
                selected_messages: selectedArray,
            };
            const response = await generatePPTForSelectedMsgs(params);
            downloadPPTFile(response.latest_pptx_file_url);
        } catch (error) {
            console.error('PPT generation Error:', error);
        } finally {
            setPptGenerating(false);
        }
    };

    const handleCancelSelection = () => {
        setIsSelectionMode(false);
        setSelectedMessages(new Set());
    };

    const enterSelectionMode = () => {
        setIsSelectionMode(true);
    };

    const handleOpenAnalysisModal = (transparency) => {
        // console.log('handleOpenAnalysisModal: Opening modal with transparency:', transparency);
        setAnalysis(transparency);
        setOpenModal(true);
    };

    const handleCloseAnalysisModal = () => {
        // console.log('handleCloseAnalysisModal: Closing modal');
        setAnalysis('');
        setOpenModal(false);
    };

    const downloadExcelFile = async (fileURL) => {
        try {
            // console.log('downloadExcelFile: Downloading', fileURL);
            const url = fileURL;
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'download.xlsx');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('downloadExcelFile: Error:', error);
        }
    };

    const downloadPPTFile = async (fileURL) => {
        try {
            // console.log('downloadPPTFile: Downloading', fileURL);
            const url = fileURL;
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'download.pptx');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('downloadPPTFile: Error:', error);
        }
    };

    const generatePPTHandler = async (msg) => {
        try {
            setPptLoading(true);

            const params = {
                session_id: chatSessionUuid,
                recharts_json: msg.additional_kwargs?.recharts_json,
                latest_question: msg.additional_kwargs?.latest_question,
                report_content: msg.content,
            };
            const response = await generatePPT(params);
            downloadPPTFile(response.latest_pptx_file_url);
        } catch (error) {
            console.error('generatePPTHandler: Error:', error);
        } finally {
            setPptLoading(false);
        }
    };

    return (
        <Container
            sx={{
                flexGrow: 1,
                overflow: 'auto',
                py: 2,
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
            }}
            className="chat-message-container"
        >
            <SelectionToolbar
                selectedCount={selectedMessages.size}
                totalCount={selectableAIMessages.length}
                onSelectAll={handleSelectAll}
                onDeselectAll={handleDeselectAll}
                onDeleteSelected={handleDeleteSelected}
                onExportSelected={handleExportSelected}
                selectedMsgPPTGeneration={handlePPTForSelected}
                onCancelSelection={handleCancelSelection}
                pptGenerating={pptGenerating}
            />

            {processedMessages.length === 0 && nodeProgress.length === 0 && (
                <Typography sx={{ color: 'gray', textAlign: 'center' }}>Start by asking a question!</Typography>
            )}
            {processedMessages.map((message, index) => (
                <Message
                    key={message.run_id || index}
                    run_id={message.run_id}
                    content={message.content}
                    isUser={message.isUser}
                    chartData={message.chartData}
                    followupQuestions={message.followupQuestions}
                    excelFileURL={message.excelFileURL}
                    pptFileURL={message.pptFileURL}
                    msgsCount={processedMessages.length}
                    index={index}
                    downloadExcelFile={downloadExcelFile}
                    downloadPPTFile={downloadPPTFile}
                    onQuestionClick={onQuestionClick}
                    isClarificationQuestion={message.transparency && Object.entries(message.transparency || {}).length}
                    openAnalysis={() => handleOpenAnalysisModal(message.transparency)}
                    transparency={message.transparency}
                    onGeneratePPTClick={() => generatePPTHandler(message)}
                    pptLoading={pptLoading}
                    nodeProgress={message.nodeProgress || []}
                    messagesEndRef={messagesEndRef}
                    isStreaming={isStreaming}
                    isFromHistory={message.isFromHistory || false}
                    isSelectionMode={isSelectionMode}
                    isSelected={!message.isUser && selectedMessages.has(message.run_id)}
                    onSelectMessage={handleSelectMessage}
                />
            ))}
            <div ref={messagesEndRef} />
            <AnswerAnalysisModal
                analysis={analysis}
                open={openModal}
                title="Analysis"
                text="SweetPeak Confections demonstrated fluctuating monthly sales performance..."
                handleClose={handleCloseAnalysisModal}
            />
        </Container>
    );
}

export default ChatMessages;
